/**
 * Talking to the Worker.
 *
 * Every call goes through {@link request}, so that a refusal always arrives as
 * an {@link ApiFailure} carrying the server's own sentence and machine code,
 * and a network fault always arrives as one with `code: 'offline'`. Callers
 * therefore never have to distinguish "the server said no" from "there was no
 * server", except where that distinction changes what to do.
 */
import type {
  HandoffAccepted,
  OrganizerOverview,
  ParticipantStatus,
  PublicState,
} from '../shared/types';
import type { ConfigInput } from '../shared/validation';

export class ApiFailure extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      // Same origin throughout — the pages and the API are one Worker — so
      // this is a statement of intent rather than a necessity. It also means a
      // misconfiguration that moved the API elsewhere would fail loudly.
      credentials: 'same-origin',
      cache: 'no-store',
      // Never follow a redirect. Nothing this API answers is a redirect, so
      // one can only have come from an identity gate in front of it — in
      // production, Cloudflare Access deciding the session has expired. Left
      // on 'follow' the browser chases it to another origin, `connect-src
      // 'self'` blocks that, and the call fails with a bare TypeError that
      // looks exactly like being offline. Caught here instead, it is a fact:
      // this needs a page load, not a retry.
      redirect: 'manual',
      ...(options.body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiFailure('offline', 'Cannot reach the server.', 0);
  }

  if (response.type === 'opaqueredirect') {
    throw new ApiFailure(
      'access_redirect',
      'Your sign-in has expired. Reloading to sign in again.',
      0,
    );
  }

  if (response.status === 204) return undefined as T;

  const isJson = (response.headers.get('Content-Type') ?? '').includes('application/json');
  const payload: unknown = isJson ? await response.json() : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiFailure(
      error?.code ?? 'server_error',
      error?.message ?? 'Something went wrong. Please try again.',
      response.status,
    );
  }
  return payload as T;
}

export interface SubmitAccepted {
  submittedAt: string;
  alreadyRecorded: boolean;
  swayUrl: string;
  /** True when the next step is `handoff`, not `swayUrl`. */
  swayHandoff: boolean;
}

export const api = {
  state: (signal?: AbortSignal): Promise<PublicState> =>
    request('/api/state', signal === undefined ? {} : { signal }),

  status: (participantId: string, signal?: AbortSignal): Promise<ParticipantStatus> =>
    request(
      `/api/status?pid=${encodeURIComponent(participantId)}`,
      signal === undefined ? {} : { signal },
    ),

  submitPre: (body: {
    participantId: string;
    answers: number[];
    instrumentVersion: number;
    email?: string;
  }): Promise<SubmitAccepted> => request('/api/submit/pre', { method: 'POST', body }),

  submitPost: (body: {
    participantId: string;
    answers: number[];
    postItemAnswer: number | null;
    instrumentVersion: number;
    email?: string;
  }): Promise<SubmitAccepted> => request('/api/submit/post', { method: 'POST', body }),

  handoff: (body: { participantId: string; email?: string }): Promise<HandoffAccepted> =>
    request('/api/handoff', { method: 'POST', body }),

  login: (password: string): Promise<void> =>
    request('/api/organizer/login', { method: 'POST', body: { password } }),

  logout: (): Promise<void> => request('/api/organizer/logout', { method: 'POST' }),

  overview: (): Promise<OrganizerOverview> => request('/api/organizer/overview'),

  saveConfig: (body: ConfigInput): Promise<OrganizerOverview> =>
    request('/api/organizer/config', { method: 'PUT', body }),

  setStage: (stage: 'pre' | 'post', open: boolean): Promise<OrganizerOverview> =>
    request('/api/organizer/stage', { method: 'POST', body: { stage, open } }),

  handover: (): Promise<OrganizerOverview> =>
    request('/api/organizer/handover', { method: 'POST' }),

  reset: (confirm: string): Promise<OrganizerOverview> =>
    request('/api/organizer/reset', { method: 'POST', body: { confirm } }),
};
